import React, { useState, useEffect } from "react";
import LoadingSpinner from "../components/LoadingSpinner";
import SocialAccountCard from "../components/SocialAccountCard";
import ConnectAccountModal from "../components/ConnectAccountModal";
import UpgradeModal from "../components/UpgradeModal";
import {
  PlusIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { socialAPI } from "../services/socialAuth";

const SocialAccounts = () => {
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [limitsData, setLimitsData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Platform configurations with capabilities
  const platforms = {
    facebook: {
      name: "Facebook",
      color: "bg-facebook",
      textColor: "text-white",
      icon: "👥",
      metrics: [
        "Posts",
        "Engagement",
        "Reach",
        "Page Insights",
        "Ad Performance",
      ],
      oauth_scope:
        "pages_show_list,pages_read_engagement,ads_read,read_insights",
    },
    instagram: {
      name: "Instagram",
      color: "bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500",
      textColor: "text-white",
      icon: "📷",
      metrics: [
        "Posts",
        "Stories",
        "Reels",
        "Followers",
        "Engagement",
        "Hashtag Performance",
      ],
      oauth_scope: "instagram_basic,instagram_manage_insights",
      businessAccountRequired: true,
      refreshFrequency: {
        standard: "Hourly",
        premium: "Real-time",
      },
      historicalData: "Up to 2 years",
    },
    threads: {
      name: "Threads",
      color: "bg-black",
      textColor: "text-white",
      icon: "🧵",
      metrics: ["Posts", "Engagement", "Follower Growth", "Thread Performance"],
      oauth_scope: "threads_basic,threads_content_publish",
    },
    twitter: {
      name: "X (Twitter)",
      color: "bg-black",
      textColor: "text-white",
      icon: "🐦",
      metrics: [
        "Tweets",
        "Engagement",
        "Impressions",
        "Follower Growth",
        "Tweet Performance",
      ],
      oauth_scope: "tweet.read,users.read,follows.read,like.read",
    },
    linkedin: {
      name: "LinkedIn",
      color: "bg-linkedin",
      textColor: "text-white",
      icon: "💼",
      metrics: [
        "Posts",
        "Engagement",
        "Profile Views",
        "Connection Growth",
        "Company Updates",
      ],
      oauth_scope: "r_liteprofile,r_emailaddress,w_member_social",
    },
    tiktok: {
      name: "TikTok",
      color: "bg-black",
      textColor: "text-white",
      icon: "🎵",
      metrics: [
        "Videos",
        "Views",
        "Engagement",
        "Follower Growth",
        "Trending Content",
      ],
      oauth_scope: "user.info.basic,video.list",
    },
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch both accounts and limits in parallel
      const [accountsResponse, limitsResponse] = await Promise.all([
        socialAPI.getAccounts(),
        socialAPI.getLimits().catch(() => null), // Graceful fallback if limits API fails
      ]);

      setConnectedAccounts(
        accountsResponse.data.accounts || accountsResponse.data || [],
      );
      setLimitsData(limitsResponse?.data || null);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setError("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectAccount = async (platform) => {
    try {
      // Check if user can add more connections via backend API
      const checkResponse = await socialAPI.checkConnectionAllowed();

      if (checkResponse.data.allowed) {
        // User can connect
        setSelectedPlatform(platform);
        setShowConnectModal(true);
      } else {
        // User reached limit, show upgrade modal
        setShowUpgradeModal(true);
      }
    } catch (err) {
      // If API returns 403, show upgrade modal
      if (err.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        console.error("Failed to check connection limits:", err);
        setError("Failed to check connection limits. Please try again.");
      }
    }
  };

  const handleDisconnect = async (accountId) => {
    if (
      !window.confirm(
        "Are you sure you want to disconnect this account? Your historical data will be preserved.",
      )
    ) {
      return;
    }

    try {
      await socialAPI.disconnect(accountId);
      setConnectedAccounts((prev) =>
        prev.filter((account) => account.id !== accountId),
      );
      // Refresh limits data after disconnect
      fetchData();
    } catch (err) {
      console.error("Failed to disconnect account:", err);
      setError("Failed to disconnect account");
    }
  };

  const handleRefreshAccount = async (accountId) => {
    try {
      setRefreshing(true);
      await socialAPI.refresh(accountId);
      await fetchData();
    } catch (err) {
      console.error("Failed to refresh account:", err);
      setError("Failed to refresh account");
    } finally {
      setRefreshing(false);
    }
  };

  const getUsageStatus = () => {
    if (!limitsData) {
      // Fallback if limits API isn't available
      const used = connectedAccounts.length;
      return {
        text: `${used} accounts connected`,
        color: "text-gray-600",
        unlimited: false,
      };
    }

    const { usage, limits } = limitsData;
    const used = usage.current_connections;
    const limit = limits.max_connections;

    if (limit >= 50) {
      return {
        text: `${used} accounts connected`,
        color: "text-green-600",
        unlimited: true,
      };
    }

    const percentage = usage.connection_usage_percentage;
    if (percentage >= 100) {
      return {
        text: `${used}/${limit} (At limit)`,
        color: "text-red-600",
      };
    }
    if (percentage >= 80) {
      return {
        text: `${used}/${limit} (Near limit)`,
        color: "text-orange-600",
      };
    }
    return {
      text: `${used}/${limit}`,
      color: "text-gray-600",
    };
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  const usageStatus = getUsageStatus();
  const canAddMore =
    limitsData?.usage?.can_add_connections ?? connectedAccounts.length < 3;
  const accountLimit =
    limitsData?.limits?.max_connections ?? 3;
  const currentUsage = limitsData?.usage?.current_connections ?? connectedAccounts.length;

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Connected Accounts
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your social media connections and track performance metrics
          </p>
        </div>

        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className={`text-sm font-medium ${usageStatus.color}`}>
              {usageStatus.text}
            </div>
            {!usageStatus.unlimited && (
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all duration-300"
                  style={{
                    width: `${Math.min((currentUsage / accountLimit) * 100, 100)}%`,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800 text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
              aria-label="Dismiss error"
            >
              <XCircleIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Recommendation */}
      {limitsData?.upgrade?.recommended && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-amber-800">
                {limitsData.upgrade.reason === "connection_limit_reached"
                  ? "Account Limit Reached"
                  : "Approaching Account Limit"}
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                You're using {currentUsage} of your {accountLimit} available accounts.
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="font-medium underline ml-1 hover:text-amber-900"
                >
                  Upgrade to add more accounts
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Platform Capabilities Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <ShieldCheckIcon className="h-5 w-5 text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-800">
              Secure OAuth Integration
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              We use industry-standard OAuth 2.0 to securely connect your
              accounts. We only access the data you explicitly grant permission
              for, and you can revoke access at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Connected Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {connectedAccounts.map((account) => (
          <SocialAccountCard
            key={account.id}
            account={account}
            platform={platforms[account.platform]}
            onDisconnect={() => handleDisconnect(account.id)}
            onRefresh={() => handleRefreshAccount(account.id)}
            isRefreshing={refreshing}
          />
        ))}

        {/* Add New Account Cards */}
        {Object.entries(platforms).map(([key, platform]) => {
          const isConnected = connectedAccounts.some(
            (account) => account.platform === key,
          );
          if (isConnected) return null;

          return (
            <div
              key={key}
              className="bg-white rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-300 transition-colors cursor-pointer group"
              onClick={() => handleConnectAccount(key)}
              role="button"
              tabIndex={0}
              aria-label={`Connect ${platform.name} account`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleConnectAccount(key);
                }
              }}
            >
              <div className="p-6 text-center">
                <div className="text-4xl mb-3" aria-hidden="true">
                  {platform.icon}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {platform.name}
                </h3>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-500">Available metrics:</p>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {platform.metrics.slice(0, 3).map((metric) => (
                      <span
                        key={metric}
                        className={`px-2 py-1 rounded text-xs ${
                          key === "instagram"
                            ? "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {metric}
                      </span>
                    ))}
                    {platform.metrics.length > 3 && (
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          key === "instagram"
                            ? "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        +{platform.metrics.length - 3} more
                      </span>
                    )}
                  </div>
                  {/* Instagram specific info */}
                  {key === "instagram" && (
                    <div className="text-center mt-3">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="text-xs text-purple-800 font-medium mb-1">
                          Business Account Required
                        </div>
                        <div className="text-xs text-purple-600">
                          Refresh: Hourly - History: 2 years
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  className={`
                    inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all
                    ${
                      canAddMore
                        ? "bg-gray-100 text-gray-700 hover:bg-gray-200 group-hover:bg-primary-600 group-hover:text-white"
                        : "bg-gray-50 text-gray-400 cursor-not-allowed"
                    }
                  `}
                  disabled={!canAddMore}
                  aria-label={
                    canAddMore
                      ? `Connect ${platform.name} account`
                      : "Upgrade required to connect more accounts"
                  }
                >
                  <PlusIcon className="w-4 h-4 mr-2" aria-hidden="true" />
                  {canAddMore ? "Connect Account" : "Upgrade to Connect"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {connectedAccounts.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4" aria-hidden="true">
            🔗
          </div>
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            No accounts connected yet
          </h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Connect your social media accounts to start tracking performance
            metrics and insights across all your platforms.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 max-w-2xl mx-auto">
            {Object.entries(platforms)
              .slice(0, 6)
              .map(([key, platform]) => (
                <button
                  key={key}
                  onClick={() => handleConnectAccount(key)}
                  className="flex flex-col items-center p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  aria-label={`Connect ${platform.name}`}
                >
                  <div className="text-2xl mb-1" aria-hidden="true">
                    {platform.icon}
                  </div>
                  <span className="text-xs text-gray-600">{platform.name}</span>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <ConnectAccountModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        platform={selectedPlatform ? platforms[selectedPlatform] : null}
        platformKey={selectedPlatform}
        onConnect={(newAccount) => {
          setConnectedAccounts((prev) => [...prev, newAccount]);
          setShowConnectModal(false);
          setSelectedPlatform(null);
          fetchData(); // Refresh limits data
        }}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={limitsData?.subscription?.tier || "free"}
        accountsUsed={currentUsage}
        accountLimit={accountLimit}
        limitsData={limitsData}
      />
    </div>
  );
};

export default SocialAccounts;
